import { Button, Checkbox, Modal, Select } from '@lifebit-ai/lds'
import React from 'react'

import * as styles from './AssignToModal.styles'
import useAssignToModal from './useAssignToModal'

export { UNASSIGN_OPTION_VALUE } from './useAssignToModal'

export const ASSIGN_TO_MODAL_TEST_ID = 'assign-to-modal'
export const ASSIGN_TO_MODAL_REVIEW_CHECKBOX_TEST_ID = 'assign-to-modal-review-checkbox'
export const ASSIGN_TO_MODAL_SUBMIT_TEST_ID = 'assign-to-modal-submit'

export interface AssignToOption {
  label: string
  value: string
}

export interface AssignToModalProps {
  open: boolean
  onClose: () => void
  /** Assignable members to choose from, already mapped to select options. */
  options: AssignToOption[]
  /** Currently assigned member id, if any. When set, an "Unassign" option is offered. */
  assignedTo?: string
  isLoadingOptions?: boolean
  isError?: boolean
  isSubmitting?: boolean
  onAssign: (assigneeId: string, reviewConfirmed: boolean) => void
  onUnassign: () => void
  /** Acting manager id. The review checkbox is hidden when assigning to yourself. */
  actingUserId?: string
}

/**
 * Presentational "Assign Request To" modal shared across request domains (export/access and
 * import requests). It owns the selection UI only; data fetching and mutations are wired by the
 * domain-specific container that renders it.
 */
const AssignToModal: React.FC<AssignToModalProps> = ({
  open,
  onClose,
  options,
  assignedTo,
  isLoadingOptions = false,
  isError = false,
  isSubmitting = false,
  onAssign,
  onUnassign,
  actingUserId
}) => {
  const {
    userId,
    setUserId,
    userOptions,
    isReviewConfirmed,
    setIsReviewConfirmed,
    selectedMemberLabel,
    isReviewConfirmationVisible,
    submitLabel,
    handleSave,
    isSaveDisabled
  } = useAssignToModal({
    open,
    options,
    assignedTo,
    isLoadingOptions,
    isError,
    isSubmitting,
    onAssign,
    onUnassign,
    actingUserId
  })

  return (
    <Modal
      data-testid={ASSIGN_TO_MODAL_TEST_ID}
      title="Assign Request To"
      open={open}
      onCancel={onClose}
      footer={
        <Button
          data-testid={ASSIGN_TO_MODAL_SUBMIT_TEST_ID}
          type="primary"
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          {submitLabel}
        </Button>
      }
    >
      <styles.Fields>
        <styles.Field>
          <styles.FieldLabel>Member</styles.FieldLabel>
          <Select
            sortOptionsByLabel="none"
            getPopupContainer={() => document.body}
            options={userOptions}
            onChange={(value: string) => setUserId(value)}
            value={userId}
          />
        </styles.Field>

        {isReviewConfirmationVisible && (
          <styles.ReviewConfirmation>
            <Checkbox
              data-testid={ASSIGN_TO_MODAL_REVIEW_CHECKBOX_TEST_ID}
              checked={isReviewConfirmed}
              label="I've reviewed this request"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setIsReviewConfirmed(event.target.checked)}
            />
            <styles.ReviewConfirmationHint>
              {`This will be recorded in the request activity${
                selectedMemberLabel ? ` and included in the email sent to ${selectedMemberLabel}` : ''
              }.`}
            </styles.ReviewConfirmationHint>
          </styles.ReviewConfirmation>
        )}
      </styles.Fields>
    </Modal>
  )
}

export default AssignToModal
